import { uniq } from 'lodash'
import {
  QueryStringFilter,
  QueryStringSort,
} from '../../../models/query-string.model'
import { Page } from '../../../models/util.model'
import {
  ReplenishmentSearchParams,
  ReplenishmentSortField,
} from '../models/replenishment-search.model'
import { Replenishment } from '../models/replenishment.entity'
import { ReplenishmentsListingKeys } from '../models/replenishment-listing.model'
import { parseFilters } from '../../../libs/filter.lib'

export function parseReplenishmentFilters(
  params: ReplenishmentSearchParams,
  filters: ReplenishmentSearchParams,
): ReplenishmentSearchParams {
  return parseFilters(params, filters, replenishmentFilterParams)
}

/**
 * Parse sort params
 * @param params - the search params
 * @param sort - the sort param to parse
 * @returns the replenishment search params updated
 */
export function replenishmentSortParams(
  params: ReplenishmentSearchParams,
  sort: QueryStringSort<ReplenishmentSortField>,
): ReplenishmentSearchParams {
  const searchParams: ReplenishmentSearchParams = {}

  switch (sort.field) {
    case 'replenishmentNumber':
    case 'createdAt':
      searchParams.sort = 'createdAt'
      searchParams.order = sort.order
      break
    case 'priority':
      searchParams.sort = 'priority'
      searchParams.order = sort.order
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

/**
 * Parse filter params
 * @param params - the search params
 * @param filter - the filter to parse
 * @returns the replenishment search params updated
 */
export function replenishmentFilterParams(
  params: ReplenishmentSearchParams,
  filter: QueryStringFilter<keyof ReplenishmentSearchParams>,
): ReplenishmentSearchParams {
  const searchParams: ReplenishmentSearchParams = {}

  switch (filter.field) {
    case '_id':
      if (filter.operator === '=') {
        searchParams._id = filter.value
      }
      break
    case 'product.SKU':
      if (filter.operator === '=') {
        searchParams['product.SKU'] = filter.value
      }
      break
    case 'status':
      if (filter.operator === '=') {
        searchParams.status = filter.value
      }
      break
    case 'userId':
      if (filter.operator === '=') {
        searchParams.userId = filter.value
      }
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

/**
 * Parse replenishment page keys
 * @param page - the replenishment page
 * @returns the page updated
 */
export function parseRepPageKeys(
  page: Page<Replenishment>,
): ReplenishmentsListingKeys {
  return page.data.reduce<ReplenishmentsListingKeys>(
    (acc, r) => parseRepKeys(r, acc),
    {},
  )
}

/**
 * Parse replenishment external data
 * @param replenishment - the replenishment
 * @param extData - the external data object
 * @returns the extData updated
 */
export function parseRepKeys(
  replenishment: Replenishment,
  extData: ReplenishmentsListingKeys = {},
): ReplenishmentsListingKeys {
  const warehouseIds = uniq([
    ...(extData['warehouseIds'] || []),
    replenishment.warehouseId,
  ])
  const productIds = uniq([
    ...(extData['productIds'] || []),
    replenishment.product!._id,
  ])

  const locationIds = extData['locationIds'] || []
  if (replenishment.source?._id) {
    locationIds.push(replenishment.source._id)
  }
  if (replenishment.target?._id) {
    locationIds.push(replenishment.target._id)
  }

  const userIds = extData['userIds'] || []
  if (replenishment.userId) {
    userIds.push(replenishment.userId)
  }

  return {
    warehouseIds,
    locationIds,
    productIds,
    userIds,
  }
}
